import {put, call, all} from 'redux-saga/effects';
import Resource from "../../entities/resource";
import * as Actions from "../actions/resourceActions";
import * as idb from '../../lib/idbd';
import * as api from '../../rest/api';
import {processQueue} from "../../queues/processQueue";
import newHRTimestamp from "../../lib/picos/timestamp";
import {ensureOperationId} from '../../lib/pendingRows';
import {tabSession} from '../../Auth/session';

// Inlined to reduce circular dependencies
function getCurrentUserId() {
    return sessionStorage.getItem('userId');
}

// Inlined to reduce circular dependencies
function getCurrentFirmId() {
    return sessionStorage.getItem('firmId');
}

type ResourceAction = {
    resourceId: string;
    resource: Resource;
    resources: Resource[];
    sentAddedOn?: number;
    onQueued?: (saved: boolean) => void;
};

export function* fetchOneResource(action: ResourceAction) {
	const session = tabSession.captureGeneration();
	try {
		const resource: Resource = yield call(api.findOne, 'resource', action.resourceId);
		session.assertCurrent();

		// Now we can update the user interface
		yield put({type: Actions.RESOURCE_FETCH_ONE_SUCCESS, resourceId: action.resourceId, resource, sentAddedOn: action.sentAddedOn});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.RESOURCE_FETCH_ONE_FAILURE, resourceId: action.resourceId, status: 500, err});

		console.error('Error fetching from database', err);
	}
}

/**
 * @param {ResourceAction} action
 */
export function* saveResource(action: ResourceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ RESOURCE_SAVE saga', action.resource?.id);

		if (action.resource === undefined || action.resource === null) {
			console.error('saveResource received a null');
			throw new Error('saveResource received a null');
		}

		if (!(action.resource instanceof Resource)) {
			action.resource = new Resource(action.resource);
		}
		// OWA-1018: the reducer showed this save at dispatch, stamped after the version this tab holds
		// (OWA-1083), marked pending under this queue key
		const dispatchedAddedOn = action.resource.addedOn;

		try {
			// Save to IndexedDB
			ensureOperationId(action.resource);
			const addResult = yield call(idb.add, 'resource', action.resource);
			if (addResult.isErr()) {
				console.error(`⮂ saveResource: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The save never reached the queue: its row returns to the latest accepted version
				yield put(Actions.revertResourceSave(action.resource, {sentAddedOn: dispatchedAddedOn}));
				yield put({type: Actions.RESOURCE_SAVE_FAILURE, resource: action.resource, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copy replaces the row shown at dispatch
			// (the queue may have stamped it again)
			yield put({type: Actions.RESOURCE_SAVE, resource: action.resource, dispatchedAddedOn, queuedAt: performance.now()});

			// A save never deletes autosaved drafts (OWA-958). The editor that wrote a draft
			// removes it when it saves; a draft typed somewhere else may be newer than this copy.
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue);
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: a save that never reached the queue returns its row to the latest accepted version
			if (!queueReported) yield put(Actions.revertResourceSave(action.resource, {sentAddedOn: dispatchedAddedOn}));
			reportQueued(false);
			yield put({type: Actions.RESOURCE_SAVE_FAILURE, resource: action.resource, status: 500, err});

			console.error('Error saving to database', err);
		}
    } finally { reportQueued(false); }
}

/**
 * TODO: Update for saveMany
 * @param {ResourceAction} action
 * @param {Resource[]} action.resources
 */
export function* saveManyResources(action: ResourceAction) {
    const session = tabSession.captureGeneration();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
    try {
        // console.debug('⮂ RESOURCE_SAVE_MANY saga', action.resources.map(resource => resource.id).join(','));

        if (!Array.isArray(action.resources)) {
            console.error('saveManyResources expected an array, received', action.resources);
            throw new Error('saveManyResources expected an array');
        }
		// OWA-1018: the reducer showed these saves at dispatch (see saveResource), under these queue keys
		const dispatchedAddedOns: number[] = action.resources.map(resource => resource?.addedOn);
		const revertAll = () => action.resources.map((resource, i) =>
			put(Actions.revertResourceSave(resource, {sentAddedOn: dispatchedAddedOns[i]})));

		try {
			const toSave: Resource[] = [];

			// Save each item to IndexedDB
			for(let i = 0; i < action.resources.length; i++) {
				const resource = action.resources[i];
				resource.persisted = false;
				ensureOperationId(resource);
				toSave.push(resource);
			}

			const addResult = yield call(idb.add, 'resource', toSave);
			if (addResult.isErr()) {
				console.error(`⮂ saveManyResources: IDB write failed: ${addResult.unwrapErr().message}`);
				reportQueued(false);
				session.assertCurrent();
				// The saves never reached the queue: their rows return to the latest accepted versions
				yield all(revertAll());
				yield put({type: Actions.RESOURCE_SAVE_MANY_FAILURE, resources: action.resources, status: 500, message: addResult.unwrapErr().message});
				return;
			}
			session.assertCurrent();
			reportQueued(true);

			// Now we can update the user interface: the queued copies replace the rows shown at dispatch
			yield put({type: Actions.RESOURCE_SAVE_MANY, resources: toSave, dispatchedAddedOns, queuedAt: performance.now()});
			session.assertCurrent();

			// Let the queue know to process
			yield call(processQueue)
		}
		catch(err) {
			if (session.signal.aborted) { reportQueued(false); return; }
			// OWA-1018: saves that never reached the queue return their rows to the latest accepted versions
			if (!queueReported) yield all(revertAll());
			reportQueued(false);
			yield put({type: Actions.RESOURCE_SAVE_MANY_FAILURE, resources: action.resources, status: 500, err});

			console.error(`Error saving ${action.resources.length} resources to database`, err);
		}
    } finally { reportQueued(false); }
}

/**
 * @param {ResourceAction} action
 */
export function* removeResource(action: ResourceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    let queueReported = false;
    const reportQueued = (saved: boolean) => {
        if (!queueReported) { queueReported = true; action.onQueued?.(saved); }
    };
	if (!userId || action.resource === undefined || action.resource === null || !(action.resource instanceof Resource)) {
			console.error('removeResource expected type Resource, received', action.resource);
			reportQueued(false);
			throw new Error('removeResource expected type Resource');
		}

	try {
		// Mark as deleted. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.resource.status = 'D';
		action.resource.addedOn = newHRTimestamp();
		action.resource.addedBy = userId;

		// Save to IndexedDB
		ensureOperationId(action.resource);
		const addResult = yield call(idb.add, 'resource', action.resource);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ removeResource: IDB write failed: ${addResult.unwrapErr().message}`);
			reportQueued(false);
			yield put({type: Actions.RESOURCE_REMOVE_FAILURE, resource: action.resource, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.RESOURCE_REMOVE, resource: action.resource});
		session.assertCurrent();

        reportQueued(true);

		// Let the queue know to process
		yield call(processQueue)
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.RESOURCE_REMOVE_FAILURE, resource: action.resource, status: 500, err});

		console.error('Error deleting from database', err);
	}
    finally { reportQueued(false); }
}

/**
	* Restore the last version of this Resource.
	*
	* @param {ResourceAction} action
	*/
export function* restoreResource(action: ResourceAction) {
    const session = tabSession.captureGeneration();
    const userId = getCurrentUserId();
    if (!userId) {
        console.error('restoreResource expected getCurrentUserId() to return a value');
        return;
    }

	if (action.resource === undefined || action.resource === null) {
        console.error('restoreResource expected a Resource, received null');
        throw new Error('restoreResource expected a Resource, received null');
    }
    else if (!(action.resource instanceof Resource)) action.resource = new Resource(action.resource); 

	try {
		// Mark as restored. The stamp follows the version this tab holds (the reducer raised the clock, OWA-1083).
		action.resource.status = 'U';
		action.resource.addedOn = newHRTimestamp();
		action.resource.addedBy = userId;
		action.resource.restore = true;

		// Save to IndexedDB
		ensureOperationId(action.resource);
		const addResult = yield call(idb.add, 'resource', action.resource);
		session.assertCurrent();
		if (addResult.isErr()) {
			console.error(`⮂ restoreResource: IDB write failed: ${addResult.unwrapErr().message}`);
			yield put({type: Actions.RESOURCE_RESTORE_FAILURE, resource: action.resource, status: 500, message: addResult.unwrapErr().message});
			return;
		}

		// Now we can update the user interface
		yield put({type: Actions.RESOURCE_RESTORE, resource: action.resource});
	}
	catch(err) {
		if (session.signal.aborted) return;
		yield put({type: Actions.RESOURCE_RESTORE_FAILURE, resource: action.resource, status: 500, err});

		console.error('Error restoring from database', err);
	}
}